import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Heart, Users, TrendingUp, AlertTriangle, Check, Sparkles } from 'lucide-react';

function LoveCompatibility() {
  const [person1, setPerson1] = useState({
    birth_date: '',
    full_name: ''
  });
  const [person2, setPerson2] = useState({
    birth_date: '',
    full_name: ''
  });

  const formatBirthDate = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Auto-add dashes: DD-MM-YYYY
    let formatted = digits;
    if (digits.length >= 2) {
      formatted = digits.slice(0, 2);
      if (digits.length >= 3) {
        formatted += '-' + digits.slice(2, 4);
        if (digits.length >= 5) {
          formatted += '-' + digits.slice(4, 8);
        }
      }
    }
    
    return formatted;
  };
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleAnalyze = async () => {
    // Validate both birth dates
    if (!person1.birth_date || !person1.birth_date.trim()) {
      setError('Person 1 birth date is required');
      return;
    }
    if (!person2.birth_date || !person2.birth_date.trim()) {
      setError('Person 2 birth date is required');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post('/api/compatibility/love', {
        person1,
        person2
      });
      
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Compatibility analysis failed');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return '#10B981';
    if (score >= 60) return '#3B82F6';
    if (score >= 45) return '#F59E0B';
    return '#FB923C';
  };

  const getPredictionIcon = (prediction) => {
    if (prediction === 'marriage') return <Heart size={48} color="#10B981" />;
    if (prediction === 'serious_relationship') return <Users size={48} color="#3B82F6" />;
    return <Sparkles size={48} color="#F59E0B" />;
  };

  return (
    <div className="page">
      <div className="container" style={{ maxWidth: '900px' }}>
        <button
          onClick={() => navigate('/dashboard')}
          className="btn btn-ghost"
          style={{ marginBottom: '2rem' }}
          data-testid="back-button"
        >
          <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
          Dashboard
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '0.5rem', textAlign: 'center' }}>
            Love Compatibility Analysis
          </h1>
          <p style={{ color: '#9CA3AF', marginBottom: '2rem', textAlign: 'center' }}>
            Discover if your energies align for marriage or friendship
          </p>
        </motion.div>

        {error && (
          <div className="alert alert-warning" style={{ marginBottom: '2rem' }}>
            {error}
          </div>
        )}

        <div className="card" style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            {/* Person 1 */}
            <div>
              <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: '#00D9FF' }}>Person 1</h3>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={person1.full_name}
                  onChange={(e) => setPerson1({ ...person1, full_name: e.target.value })}
                  placeholder="Enter name"
                  data-testid="person1-name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Birth Date (DD-MM-YYYY)</label>
                <input
                  type="text"
                  className="form-input"
                  value={person1.birth_date}
                  onChange={(e) => setPerson1({ ...person1, birth_date: formatBirthDate(e.target.value) })}
                  placeholder="22-05-1994"
                  maxLength="10"
                  data-testid="person1-dob"
                />
              </div>
            </div>

            {/* Person 2 */}
            <div>
              <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: '#7C3AED' }}>Person 2</h3>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={person2.full_name}
                  onChange={(e) => setPerson2({ ...person2, full_name: e.target.value })}
                  placeholder="Enter name"
                  data-testid="person2-name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Birth Date (DD-MM-YYYY)</label>
                <input
                  type="text"
                  className="form-input"
                  value={person2.birth_date}
                  onChange={(e) => setPerson2({ ...person2, birth_date: formatBirthDate(e.target.value) })}
                  placeholder="15-08-1990"
                  maxLength="10"
                  data-testid="person2-dob"
                />
              </div>
            </div>
          </div>

          <button
            onClick={handleAnalyze}
            className="btn btn-primary"
            disabled={loading}
            data-testid="analyze-compatibility"
            style={{ width: '100%', marginTop: '1.5rem' }}
          >
            {loading ? 'Analyzing Compatibility...' : 'Analyze Compatibility'}
          </button>
        </div>

        {result && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            {/* Overall Score */}
            <div className="card" style={{ 
              textAlign: 'center', 
              marginBottom: '2rem',
              background: `linear-gradient(135deg, rgba(${getScoreColor(result.scores.total) === '#10B981' ? '16, 185, 129' : '59, 130, 246'}, 0.1) 0%, rgba(0, 0, 0, 0.05) 100%)`,
              border: `2px solid ${getScoreColor(result.scores.total)}`
            }}>
              <div style={{ marginBottom: '1.5rem' }}>
                {getPredictionIcon(result.prediction)}
              </div>
              
              <h2 style={{ fontSize: '2rem', marginBottom: '0.5rem', color: getScoreColor(result.scores.total) }}>
                {result.prediction_text}
              </h2>
              
              <div style={{ 
                width: '150px', 
                height: '150px', 
                margin: '1.5rem auto',
                borderRadius: '50%',
                border: `8px solid ${getScoreColor(result.scores.total)}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: `rgba(${getScoreColor(result.scores.total) === '#10B981' ? '16, 185, 129' : '59, 130, 246'}, 0.1)`
              }}>
                <div style={{ fontSize: '3rem', fontWeight: 700, color: getScoreColor(result.scores.total) }}>
                  {result.scores.total}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>/ 100</div>
              </div>

              <p style={{ fontSize: '1.125rem', lineHeight: 1.7, marginBottom: '1rem', maxWidth: '600px', margin: '0 auto 1.5rem' }}>
                {result.summary}
              </p>

              <div className="badge" style={{ 
                background: `rgba(${getScoreColor(result.scores.total) === '#10B981' ? '16, 185, 129' : '59, 130, 246'}, 0.2)`,
                color: getScoreColor(result.scores.total),
                padding: '0.5rem 1.5rem',
                fontSize: '1rem'
              }}>
                {result.relationship_type}
              </div>
            </div>

            {/* Planetary Dynamics */}
            <div className="card" style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Planetary Dynamics</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div style={{ padding: '1.5rem', background: '#1E2139', borderRadius: '12px', textAlign: 'center' }}>
                  <h4 style={{ fontSize: '1rem', color: '#9CA3AF', marginBottom: '1rem' }}>Mind to Mind</h4>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {result.analysis.planetary_dynamics.mind_to_mind}
                  </p>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: getScoreColor(result.scores.driver_compatibility) }}>
                    {result.scores.driver_compatibility}%
                  </div>
                </div>

                <div style={{ padding: '1.5rem', background: '#1E2139', borderRadius: '12px', textAlign: 'center' }}>
                  <h4 style={{ fontSize: '1rem', color: '#9CA3AF', marginBottom: '1rem' }}>Destiny to Destiny</h4>
                  <p style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {result.analysis.planetary_dynamics.destiny_to_destiny}
                  </p>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: getScoreColor(result.scores.conductor_compatibility) }}>
                    {result.scores.conductor_compatibility}%
                  </div>
                </div>
              </div>

              <div style={{ padding: '1.5rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px', borderLeft: '4px solid #3B82F6' }}>
                <h4 style={{ fontSize: '1.125rem', marginBottom: '0.5rem', color: '#3B82F6' }}>Harmony Level</h4>
                <p style={{ fontSize: '1.5rem', fontWeight: 600 }}>{result.analysis.planetary_dynamics.harmony_level}</p>
              </div>
            </div>

            {/* Strengths */}
            {result.strengths && result.strengths.length > 0 && (
              <div className="card" style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Check size={24} color="#10B981" />
                  Relationship Strengths
                </h3>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {result.strengths.map((strength, i) => (
                    <div key={i} style={{ 
                      padding: '1rem', 
                      background: 'rgba(16, 185, 129, 0.1)', 
                      borderRadius: '8px',
                      borderLeft: '3px solid #10B981',
                      fontSize: '1rem',
                      lineHeight: 1.6
                    }}>
                      ✓ {strength}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Challenges */}
            {result.challenges && result.challenges.length > 0 && (
              <div className="card" style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <AlertTriangle size={24} color="#FB923C" />
                  Areas Needing Attention
                </h3>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {result.challenges.map((challenge, i) => (
                    <div key={i} style={{ 
                      padding: '1rem', 
                      background: 'rgba(251, 146, 60, 0.1)', 
                      borderRadius: '8px',
                      borderLeft: '3px solid #FB923C',
                      fontSize: '1rem',
                      lineHeight: 1.6
                    }}>
                      ! {challenge}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Advice */}
            <div className="card" style={{ 
              background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
              border: '2px solid rgba(124, 58, 237, 0.3)'
            }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Relationship Advice</h3>
              <p style={{ fontSize: '1.0625rem', lineHeight: 1.8 }}>
                {result.advice}
              </p>
            </div>

            {/* Complementary Energies */}
            {result.analysis?.complementary_energies && (
              <div className="card" style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Complementary Energies</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                  <div style={{ padding: '1.5rem', background: 'rgba(0, 217, 255, 0.1)', borderRadius: '12px' }}>
                    <h4 style={{ fontSize: '1.125rem', marginBottom: '0.75rem', color: '#00D9FF' }}>
                      {result.person1.name} Provides
                    </h4>
                    {result.analysis.complementary_energies.person1_provides.length > 0 ? (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {result.analysis.complementary_energies.person1_provides.map(num => (
                          <span key={num} className="badge badge-primary" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                            {num}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: '#9CA3AF', fontSize: '0.875rem' }}>No missing energies to fill</p>
                    )}
                  </div>

                  <div style={{ padding: '1.5rem', background: 'rgba(124, 58, 237, 0.1)', borderRadius: '12px' }}>
                    <h4 style={{ fontSize: '1.125rem', marginBottom: '0.75rem', color: '#7C3AED' }}>
                      {result.person2.name} Provides
                    </h4>
                    {result.analysis.complementary_energies.person2_provides.length > 0 ? (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {result.analysis.complementary_energies.person2_provides.map(num => (
                          <span key={num} className="badge" style={{ 
                            background: 'rgba(124, 58, 237, 0.2)', 
                            color: '#7C3AED',
                            fontSize: '1rem',
                            padding: '0.5rem 1rem'
                          }}>
                            {num}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: '#9CA3AF', fontSize: '0.875rem' }}>No missing energies to fill</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default LoveCompatibility;
